import * as React from 'react';
import styled from 'styled-components';
import {SystemIcons} from '../icons';
import {COLORS, ComponentTextStyle, focusStyles} from '../styles';
import {Size, Testable} from '../types';
import {ComponentLStyling, ComponentMStyling, ComponentSStyling, ComponentXXSStyling} from '../styles';
import {defaultOnMouseDownHandler} from '../common';

const StyledRadioButton = styled.div<{ $margin?: string, $disabled?: boolean, $invalid?: boolean, $selected?: boolean }>`
  display: flex;
  width: 100%;
  min-height: 48px;
  min-width: 48px;

  ${props => (props.$margin ? `margin: ${props.$margin};` : '')}
  cursor: pointer;

  color: ${props => COLORS.generateToken({componentType:'text', defaultVariant:'default'}, props.theme)};

  .pointerTransparent {
    pointer-events: none;
  }

  .radio-button-icon {
    margin: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    height: 36px;
    min-width: 36px;
    border-radius: 50%;

    svg {
      color: ${props => (props.$selected ? COLORS.generateToken({componentType:'icon', defaultVariant:'selected'}, props.theme) : 
        props.$invalid ? COLORS.generateToken({componentType:'icon', defaultVariant:'critical'}, props.theme) : COLORS.generateToken({componentType:'icon',defaultVariant:'subtle'}, props.theme))};
    }
  }

  .radio-button-label {
    user-select: none;
    display: flex;
    flex-direction: column;

    label {
      cursor: inherit;
    }

    span {
      ${props => ComponentXXSStyling(ComponentTextStyle.Regular, COLORS.generateToken({componentType:'text', defaultVariant:'default'}, props.theme))}
    }
  }

  &.small {
    ${props => ComponentSStyling(ComponentTextStyle.Regular, COLORS.generateToken({componentType:'text', defaultVariant:'default'}, props.theme))}
    .radio-button-label {
      padding-top: 14px;
    }
  }

  &.medium {
    ${props => ComponentMStyling(ComponentTextStyle.Regular, COLORS.generateToken({componentType:'text', defaultVariant:'default'}, props.theme))}
    .radio-button-label {
      padding-top: 12px;
    }
  }

  &.large {
    ${props => ComponentLStyling(ComponentTextStyle.Regular, COLORS.generateToken({componentType:'text', defaultVariant:'default'}, props.theme))}
    .radio-button-label {
      padding-top: 12px;
    }
  }

  &:not(.disabled):focus {
    ${focusStyles}
  }

  &:not(.disabled):hover {
    .radio-button-icon {
      background-color: ${props => COLORS.generateToken({ componentType:'bg-surface', state:'hover' }, props.theme)};

      svg {
        color: ${props => COLORS.generateToken({ componentType:'icon', state:'hover' }, props.theme)};
      }
    }
  }

  &:not(.disabled):active {
    .radio-button-icon {
      background: ${props => COLORS.generateToken({ componentType:'bg-surface', state:'active' }, props.theme)};

      svg {
        color: ${props => COLORS.generateToken({ componentType:'icon', state:'active' }, props.theme)};
      }
    }
  }

  &.disabled {
    cursor: not-allowed;

    color: ${props => COLORS.generateToken({ componentType: 'text', state: 'disabled' }, props.theme)};

    .radio-button-icon,
    .radio-button-label {
      pointer-events: none;
    }

    .radio-button-icon {
      background-color: transparent;
    }

    .radio-button-icon svg {
      color: ${props => COLORS.generateToken({ componentType: 'icon', state: 'disabled' }, props.theme)};
    }
  }

  &.dropdown-hover:not(.disabled) {
    .radio-button-icon {
      background-color: ${props => COLORS.generateToken({ componentType: 'bg-surface', state: 'hover' }, props.theme)};

      svg {
        color: ${props => COLORS.generateToken({ componentType: 'icon', state: 'hover' }, props.theme)};
      }
    }
  }
`;

export type RadioButtonProps = Testable & Omit<React.HTMLAttributes<HTMLDivElement>, 'tabIndex' | 'className' | 'onClick' | 'onMouseDown' | 'onKeyDown'> & {
  /** Optional. Id of the component */
  id?: string;
  /** Required. Current state of the component. */
  selected: boolean;
  /** Required. Handler to be called when user selects/unselected the radio button. */
  select: (selected: boolean) => void;
  /** Optional. Label to be shown on the right side of the radio button. */
  label?: string | React.ReactNode;
  /** Optional. Secondary label to be shown under the main 'label' */
  additionalLabel?: string | React.ReactNode;
  /** Optional. If set, then if radiobutton is in unselected state it will be shown in invalid state. */
  invalid?: boolean;
  /** Optional. If disabled, user can not interact with the component. */
  disabled?: boolean;
  /** Optional. If set, then 'pointer-events: none' will be set on the component. */
  iconPointerEventsTransparent?: boolean;
  /** Optional. Margin style attribute to be set on the top level tag of the component. */
  margin?: string;
  /** Optional. Size of the radio button, defaults to 'medium'. */
  size?: Size.Small | Size.Medium | Size.Large;
  /** Optional. TabIndex attribute to be set on the component. */
  tabIndexVal?: number;
  /** Optional. Extra classname to be set on the container of the component. */
  className?: string;
}

const RadioButton = React.forwardRef<HTMLDivElement, RadioButtonProps>(({
                                                               id,
                                                               selected,
                                                               label,
                                                               additionalLabel,
                                                               invalid,
                                                               select,
                                                               disabled,
                                                               margin,
                                                               iconPointerEventsTransparent,
                                                               size,
                                                               tabIndexVal,
                                                               className,
                                                               dataTestId,
                                                               ...rest
                                                             }: RadioButtonProps, ref) => {
  const onKeyPress = (e: any) => {
    if (e.keyCode === 13 && !disabled) {
      select(!selected);
    }
  };

  size = size ?? Size.Medium;

  const cls = `${size} ${className || ''} ${disabled ? ' disabled' : ''}`;

  return (
    <StyledRadioButton key={id}
                       $margin={margin}
                       ref={ref}
                       onClick={() => !disabled && select(!selected)}
                       onMouseDown={defaultOnMouseDownHandler}
                       onKeyDown={onKeyPress}
                       $disabled={disabled}
                       className={cls}
                       tabIndex={disabled ? -1 : (tabIndexVal ? tabIndexVal : 0)}
                       $invalid={invalid}
                       $selected={selected}
                       {...rest}>
      <div className={'radio-button-icon'} id={id} data-testid={dataTestId}>
        {selected && <SystemIcons.RadioButtonOn className={iconPointerEventsTransparent ? 'pointerTransparent' : ''} size="24px"/>}
        {!selected &&
          <SystemIcons.RadioButtonOff className={iconPointerEventsTransparent ? 'pointerTransparent' : ''} size="24px"/>}
      </div>
      <div className={'radio-button-label'}>
        {label && <label htmlFor={id}>{label}</label>}
        {additionalLabel !== undefined && <span>{additionalLabel}</span>}
      </div>
    </StyledRadioButton>
  );
});

export default RadioButton;
